import React, { useEffect, useState } from "react";
import { Bell, Package, MapPin, Truck } from "lucide-react";
import { Btn, fmt } from "./ui";
import { CONFIG_TIMEOUT } from "../constants/timeouts";

// Popup full-width estilo Uber pra notificações urgentes de novo pedido.
// Timer regressivo dispara onRecusar() automaticamente quando chega a 0
// (rotação automática pro próximo freteiro/locador na fila).
//
// Visual (plano de layout, 24/09): cartão BRANCO com faixa de tempo no topo,
// sem gradiente, sem pulsar/quicar, botões do `Btn`. A urgência aparece na
// cor da faixa e do relógio, não em animação.
//
// Dois layouts:
// - tipo='locador': barra horizontal de tempo + 3 botões (Recusar/
//   Detalhes/Aceitar). Tempo em mm:ss. 30min total.
// - tipo='freteiro': timer circular grande (estilo Uber) + valor em
//   destaque + rota origem→destino + métricas (km/kg/min). 90s total.
export const NotificacaoPedidoPopup = ({ pedido, tipo, onAceitar, onRecusar, onVerDetalhes, onTimeout, tempoTotalSegundos }) => {
  // tempoTotalSegundos: override opcional do tempo do "toque". Quando o popup
  // é usado como CHAMADA global (toca em qualquer tela), passamos um tempo
  // curto (ex.: 45s) — assim o overlay não fica preso 30min na tela. Sem o
  // override, mantém o comportamento antigo (30min locador / 90s freteiro).
  const [tempoRestante, setTempoRestante] = useState(
    tempoTotalSegundos != null
      ? tempoTotalSegundos
      : (tipo === 'locador' ? CONFIG_TIMEOUT.locador.tempoTotal * 60 : CONFIG_TIMEOUT.freteiro.tempoTotal)
  );

  useEffect(() => {
    if (tempoRestante <= 0) {
      // onTimeout (quando passado) só DISPENSA o chamado — não recusa o
      // pedido via API. Sem ele, cai no comportamento antigo (onRecusar).
      (onTimeout || onRecusar)?.();
      return;
    }
    const timer = setInterval(() => {
      setTempoRestante((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  // onTimeout/onRecusar sao callbacks estaveis - incluir dispararia o
  // effect a cada render do pai. tempoRestante eh o unico trigger real.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempoRestante]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return tipo === 'locador'
      ? `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      : `${seconds}s`;
  };

  const tempoTotal = tempoTotalSegundos != null
    ? tempoTotalSegundos
    : (tipo === 'locador' ? CONFIG_TIMEOUT.locador.tempoTotal * 60 : CONFIG_TIMEOUT.freteiro.tempoTotal);
  const progresso = ((tempoTotal - tempoRestante) / tempoTotal) * 100;
  const corBarra = tempoRestante <= tempoTotal * 0.2 ? '#ef4444' : tempoRestante <= tempoTotal * 0.5 ? '#f59e0b' : '#f97316';
  const isUrgente = tempoRestante <= tempoTotal * 0.3;

  const img = pedido?.produto?.img;
  const imgEhUrl = typeof img === 'string' && /^(https?:|data:|blob:)/.test(img);

  if (tipo === 'locador') {
    return (
      <div className="fixed inset-x-0 top-0 z-[1300] p-4 safe-area-top" role="alertdialog" aria-label="Nova solicitação de locação">
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-2xl shadow-[0_12px_40px_rgba(2,6,23,0.25)] ring-1 ring-black/5 overflow-hidden animate-[toastIn_.22s_ease-out]">
            <div className="h-1.5 bg-slate-100">
              <div
                className="h-full transition-all duration-1000"
                style={{ width: `${100 - progresso}%`, backgroundColor: corBarra }}
              />
            </div>

            <div className="p-4">
              <div className="flex items-center justify-between mb-3 gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <Bell size={20} className="text-orange-500" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-[#12192a] text-sm">Nova solicitação de locação</h3>
                    <p className="text-xs text-slate-500 truncate">Pedido #{pedido?.numero || pedido?.id || '—'}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`font-mono text-2xl font-extrabold tabular-nums ${isUrgente ? 'text-red-600' : 'text-[#12192a]'}`}>
                    {formatTime(tempoRestante)}
                  </p>
                  <p className="text-[10px] text-slate-400">para responder</p>
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-3 mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white border border-slate-200 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                    {imgEhUrl ? <img src={img} alt="" className="w-full h-full object-cover" /> : <Package size={20} className="text-slate-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#12192a] text-sm truncate">{pedido?.produto?.nome || 'Produto'}</p>
                    <p className="text-xs text-slate-500 truncate">{pedido?.dias || 1} dia{(pedido?.dias || 1) === 1 ? '' : 's'}{pedido?.cliente?.nome ? ` · ${pedido.cliente.nome}` : ''}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-extrabold text-[#12192a] text-lg">{fmt(pedido?.total || 0)}</p>
                    {pedido?.caucao > 0 && <p className="text-[10px] text-slate-400">+{fmt(pedido.caucao)} caução</p>}
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Btn v="white" sz="md" className="flex-1" onClick={onRecusar}>Recusar</Btn>
                <Btn v="white" sz="md" className="flex-1" onClick={onVerDetalhes}>Detalhes</Btn>
                <Btn v="primary" sz="md" className="flex-1" onClick={onAceitar}>Aceitar</Btn>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Notificação para FRETEIRO
  return (
    <div className="fixed inset-x-0 top-0 z-[1300] p-4 safe-area-top" role="alertdialog" aria-label="Novo frete">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-2xl shadow-[0_12px_40px_rgba(2,6,23,0.25)] ring-1 ring-black/5 overflow-hidden animate-[toastIn_.22s_ease-out]">
          {/* Timer circular no topo */}
          <div className="relative flex items-center justify-center pt-5 pb-3">
            <div className="relative w-20 h-20">
              <svg className="w-full h-full transform -rotate-90">
                <circle cx="40" cy="40" r="36" stroke="#e2e8f0" strokeWidth="6" fill="none" />
                <circle
                  cx="40" cy="40" r="36"
                  stroke={corBarra}
                  strokeWidth="6" fill="none" strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 36}`}
                  strokeDashoffset={`${2 * Math.PI * 36 * (progresso / 100)}`}
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-2xl font-extrabold tabular-nums ${isUrgente ? 'text-red-600' : 'text-[#12192a]'}`}>
                  {tempoRestante}
                </span>
              </div>
            </div>
            <div className="absolute top-3 right-4 flex items-center gap-1.5 bg-orange-50 text-orange-700 rounded-full px-2.5 py-1">
              <Truck size={12} />
              <span className="text-[10px] font-bold uppercase tracking-wide">Novo frete</span>
            </div>
          </div>

          <div className="px-4 pb-4">
            <div className="text-center mb-3">
              <p className="text-slate-500 text-xs">Valor do frete</p>
              <p className="text-3xl font-extrabold text-[#12192a]">{fmt(pedido?.valorFrete || 0)}</p>
            </div>

            <div className="bg-slate-50 rounded-xl p-3 mb-3">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-6 h-6 bg-white border border-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <Package size={12} className="text-slate-600" />
                </div>
                <span className="text-slate-700 flex-1 truncate">{pedido?.origem?.nome || 'Local de coleta'}</span>
              </div>
              <div className="ml-3 border-l-2 border-slate-200 h-3" />
              <div className="flex items-center gap-2 text-sm">
                <div className="w-6 h-6 bg-orange-50 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin size={12} className="text-orange-500" />
                </div>
                <span className="text-slate-700 flex-1 truncate">{pedido?.destino?.nome || 'Destino'}</span>
              </div>
            </div>

            <div className="flex justify-around text-center mb-3">
              <div>
                <p className="font-bold text-[#12192a]">{pedido?.distanciaTotal ?? '—'} km</p>
                <p className="text-[10px] text-slate-400">Distância</p>
              </div>
              <div>
                <p className="font-bold text-[#12192a]">{pedido?.pesoTotal ?? '—'} kg</p>
                <p className="text-[10px] text-slate-400">Peso</p>
              </div>
              <div>
                <p className="font-bold text-[#12192a]">{pedido?.tempoEstimado != null ? `~${pedido.tempoEstimado} min` : '—'}</p>
                <p className="text-[10px] text-slate-400">Tempo</p>
              </div>
            </div>

            <div className="flex gap-2">
              <Btn v="white" className="flex-1" onClick={onRecusar}>Recusar</Btn>
              <Btn v="primary" className="flex-1" onClick={onAceitar}>Aceitar frete</Btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
